import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WithTranslation, withTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useMytaverse } from '@providers/MytaverseProvider';
import ConfirmModal from '@components/ConfirmModal';
import ROUTES from '@constants/routes';
import ParticipantsService from '@services/ParticipantsService';
import { AvatarTypes } from '@interfaces/avatar';
import { useAvaturnHelper } from '@features/avatar/helpers';

const UnfinishedAvatarModal = ({ t: translate }: WithTranslation) => {
  const { eventId: currentEventId } = useParams();

  const [showModal, setShowModal] = useState(false);
  const [isDeleteProcessing, setIsDeleteProcessing] = useState(false);

  const navigate = useNavigate();
  const { getProcessedBrand } = useAvaturnHelper();
  const { pathname } = useLocation();

  const {
    unfinishedAvatar,
    setUnfinishedAvatar,
    setAvaturnToProcess,
    setIsCreatingGenericAvaturn,
  } = useMytaverse();

  const onClose = useCallback(async () => {
    if (!unfinishedAvatar?.avatarId) {
      return;
    }
    try {
      setIsDeleteProcessing(true);

      const processedBrand = getProcessedBrand();
      await ParticipantsService.deleteParticipantAvatarFromList({
        avatarType: AvatarTypes.AVATURN,
        avatarId: unfinishedAvatar.avatarId,
        brand: processedBrand,
        hardDelete: true,
      });

      setUnfinishedAvatar(null);
      setShowModal(false);
      setIsDeleteProcessing(false);
    } catch {
      setIsDeleteProcessing(false);
    }
  }, [unfinishedAvatar, getProcessedBrand]);

  const onEditClick = useCallback(async () => {
    if (!unfinishedAvatar) {
      return;
    }

    setAvaturnToProcess({
      avatarId: unfinishedAvatar.avatarId,
      avaturnId: '',
      isNew: true,
    });
    setIsCreatingGenericAvaturn(false);

    navigate(ROUTES.AVATURN(currentEventId));
    setUnfinishedAvatar(null);
    setShowModal(false);
  }, [unfinishedAvatar, currentEventId]);

  useEffect(() => {
    if (unfinishedAvatar && pathname.includes('avatars')) {
      setShowModal(true);
    }
  }, [unfinishedAvatar, pathname]);

  const message = (
    <>
      <span>{translate('unfinishedAvatar.description1')}</span>
      {isMobileOnly ? ' ' : <br />}
      <span>{translate('unfinishedAvatar.description2')}</span>
    </>
  );

  return (
    <ConfirmModal
      icon={<PriorityHighIcon />}
      open={showModal}
      title={translate('unfinishedAvatar.title')}
      message={message}
      onConfirm={onClose}
      onCancel={onEditClick}
      confirmButtonName={translate('unfinishedAvatar.delete')}
      cancelButtonName={translate('unfinishedAvatar.edit')}
      isConfirmLoading={isDeleteProcessing}
    />
  );
};

export default withTranslation()(UnfinishedAvatarModal);
